import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import PDFViewer from "./PDFViewer";
import { esignatureService } from "../../services/esignature";
import { getApiEndpoint } from "../../config/config";
import "./RecipientSidebar.css";
import { handleGenericError } from "../../utils/errorHandler";

const DEFAULT_COLORS = [
  "#4A90E2",
  "#E24A4A",
  "#4AE290",
  "#E2904A",
  "#904AE2",
  "#E2E24A",
  "#4AE2E2",
  "#E24A90",
];

const DEFAULT_TEMPLATE_ROLES = [];

const TemplateEditor = () => {
  const { templateId, documentId } = useParams();
  const navigate = useNavigate();
  const editingId = templateId || documentId;
  const isEditingDocument = !!documentId;
  const { getVerifiedJWT } = useAuth();
  const { showSuccess, showError } = useToast();
  const [template, setTemplate] = useState(null);
  const [fields, setFields] = useState([]);
  const [selectedField, setSelectedField] = useState(null);
  const [roles, setRoles] = useState(DEFAULT_TEMPLATE_ROLES);
  const [selectedRole, setSelectedRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddRoleModal, setShowAddRoleModal] = useState(false);
  const [showCCWarning, setShowCCWarning] = useState(false);
  const [showEditRoleModal, setShowEditRoleModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [lastFieldType, setLastFieldType] = useState("signature");
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInputValue, setPageInputValue] = useState("1");
  const [pageCanvases, setPageCanvases] = useState([]);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const pdfViewerRef = useRef(null);
  const saveTimeoutRef = useRef(null);
  const saveInProgressRef = useRef(false);
  const saveQueueRef = useRef([]);
  const currentFieldsRef = useRef([]);

  useEffect(() => {
    loadTemplate();
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveQueueRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  // Load available groups and assigned groups when template is loaded
  useEffect(() => {
    if (template && !isEditingDocument) {
      loadAvailableGroups();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template]);

  // Add keyboard event listener for delete
  useEffect(() => {
    const handleKeyDown = e => {
      if ((e.key === "Delete" || e.key === "Backspace") && selectedField) {
        // Prevent default backspace navigation
        e.preventDefault();
        handleFieldDelete();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedField]);

  useEffect(() => {
    currentFieldsRef.current = fields;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields]);

  const cleanField = field => ({
    field_type: field.field_type,
    label: field.label,
    required: field.required,
    page_number: field.page_number,
    x_position: field.x_position,
    y_position: field.y_position,
    width: field.width,
    height: field.height,
    assigned_to_recipient_role: field.assigned_to_recipient_role,
    dropdown_options: field.dropdown_options,
    default_value: field.default_value,
    // Include page dimensions for proper coordinate scaling on reload
    page_width: field.page_width,
    page_height: field.page_height,
  });

  const processSaveQueue = useCallback(async () => {
    if (saveInProgressRef.current || saveQueueRef.current.length === 0) {
      return;
    }

    saveInProgressRef.current = true;
    const fieldsToSave = saveQueueRef.current.shift();

    try {
      const jwt = await getVerifiedJWT();
      const cleanFields = fieldsToSave.map(cleanField);

      // Use correct API endpoint based on whether editing document or template
      if (isEditingDocument) {
        await esignatureService.addFields(editingId, cleanFields, jwt);
      } else {
        await esignatureService.addFieldsToTemplate(
          editingId,
          cleanFields,
          jwt
        );
      }
    } catch (error) {
      // Error saving fields
    } finally {
      saveInProgressRef.current = false;
      if (saveQueueRef.current.length > 0) {
        processSaveQueue();
      }
    }
  }, [editingId, isEditingDocument, getVerifiedJWT]);

  const queueSave = useCallback(
    fieldsToSave => {
      saveQueueRef.current = [fieldsToSave];
      processSaveQueue();
    },
    [processSaveQueue]
  );

  const loadTemplate = async () => {
    try {
      setLoading(true);
      const jwt = await getVerifiedJWT();

      // Load either template or document based on which ID is present
      const response = isEditingDocument
        ? await esignatureService.getDocument(editingId, jwt)
        : await esignatureService.getTemplate(editingId, jwt);

      // Unwrap the response - backend returns {success: true, data: {...}}
      const templateData = response.data;

      setTemplate(templateData);

      // Process fields and log commission scale data for debugging
      const processedFields = (templateData.fields || []).map(field => {
        return field;
      });

      setFields(processedFields);

      // Load roles from roles_metadata if available, otherwise extract from fields
      if (
        templateData.roles_metadata &&
        templateData.roles_metadata.length > 0
      ) {
        // Ensure all roles have unique IDs
        const rolesWithIds = templateData.roles_metadata.map((role, index) => ({
          ...role,
          id: role.id || `role-${index + 1}-${Date.now()}`,
        }));
        setRoles(rolesWithIds);
        // Default to first role (Direct Seller)
        const firstRole =
          rolesWithIds.find(r => r.full_name === "Direct Seller") ||
          rolesWithIds[0];
        setSelectedRole(firstRole);
      } else if (templateData.fields && templateData.fields.length > 0) {
        const uniqueRoles = [
          ...new Set(
            templateData.fields.map(f => f.assigned_to_recipient_role)
          ),
        ];
        const loadedRoles = uniqueRoles.map((roleName, index) => ({
          id: `role-${index + 1}`,
          role: roleName,
          full_name: roleName,
          email: `${roleName.toLowerCase()}@example.com`,
          color: DEFAULT_COLORS[index % DEFAULT_COLORS.length],
          signing_order: index + 1,
        }));

        if (loadedRoles.length > 0) {
          setRoles(loadedRoles);
          setSelectedRole(loadedRoles[0]);
        }
      }
    } catch (error) {
      alert("Failed to load template");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectRole = role => {
    setSelectedRole(role);
  };

  const handleEditRole = (role, e) => {
    e.stopPropagation();
    setEditingRole(role);
    setShowEditRoleModal(true);
  };

  const handleUpdateRole = updatedRoleData => {
    const updatedRoles = roles.map(r =>
      r.id === editingRole.id
        ? {
            ...r,
            full_name: updatedRoleData.name,
            role: updatedRoleData.type,
            color: updatedRoleData.color,
          }
        : r
    );
    setRoles(updatedRoles);
    saveRolesToTemplate(updatedRoles);

    // Update selected role if it was the one being edited
    if (selectedRole?.id === editingRole.id) {
      setSelectedRole({
        ...selectedRole,
        full_name: updatedRoleData.name,
        role: updatedRoleData.type,
        color: updatedRoleData.color,
      });
    }

    // No need to update field assignments - they're tied to recipient ID, not role type

    setShowEditRoleModal(false);
    setEditingRole(null);
  };

  const getFieldLabel = fieldType => {
    const labelMap = {
      signature: "Signature",
      initial: "Initial",
      text: "Text",
      email: "Email",
      phone: "Phone Number",
      checkbox: "Checkbox",
      date: "Date",
    };
    return labelMap[fieldType] || fieldType;
  };

  const handleFieldAdd = newField => {
    if (!selectedRole) {
      alert("Please select a role first before adding fields.");
      return;
    }

    const field = {
      ...newField,
      id: `temp-${Date.now()}`,
      field_type: lastFieldType,
      label: getFieldLabel(lastFieldType),
      assigned_to_recipient_role: selectedRole.id, // Use unique ID instead of role type
      required: true,
    };
    setFields(prevFields => {
      const updated = [...prevFields, field];
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      queueSave(updated);
      return updated;
    });
    setSelectedField(field);
  };

  const handleFieldMove = (fieldId, position) => {
    setFields(prevFields => {
      const updated = prevFields.map(f =>
        f.id === fieldId
          ? {
              ...f,
              ...(position.x_position !== undefined && {
                x_position: position.x_position,
              }),
              ...(position.y_position !== undefined && {
                y_position: position.y_position,
              }),
              // Include width and height if provided (for resize operations)
              ...(position.width !== undefined && { width: position.width }),
              ...(position.height !== undefined && { height: position.height }),
              // Include page_number if provided (for cross-page dragging)
              ...(position.page_number !== undefined && {
                page_number: position.page_number,
              }),
              // Include default_value if provided (for text editing)
              ...(position.default_value !== undefined && {
                default_value: position.default_value,
              }),
            }
          : f
      );

      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        queueSave(currentFieldsRef.current);
      }, 1000);

      return updated;
    });
  };

  const handleFieldUpdate = updatedField => {
    setFields(prevFields => {
      const updated = prevFields.map(f =>
        f.id === updatedField.id ? updatedField : f
      );

      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        queueSave(currentFieldsRef.current);
      }, 500);

      return updated;
    });
    setSelectedField(updatedField);
  };

  const handleFieldDelete = async () => {
    if (!selectedField) return;

    // Clear any pending save operations
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }

    // Clear the save queue
    saveQueueRef.current = [];

    // Remove field from local state immediately
    const updated = fields.filter(f => f.id !== selectedField.id);
    setFields(updated);
    setSelectedField(null);

    // Wait for any in-progress saves to complete
    while (saveInProgressRef.current) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // Only sync to backend if this isn't a temporary field
    // (temporary fields haven't been saved to the backend yet)
    const isTemporaryField =
      selectedField.id && selectedField.id.toString().startsWith("temp-");

    if (!isTemporaryField) {
      try {
        const jwt = await getVerifiedJWT();
        const cleanFields = updated.map(cleanField);

        // Use correct API endpoint based on whether editing document or template
        if (isEditingDocument) {
          await esignatureService.addFields(editingId, cleanFields, jwt);
        } else {
          await esignatureService.addFieldsToTemplate(
            editingId,
            cleanFields,
            jwt
          );
        }
      } catch (error) {
        alert("Failed to delete field");
        loadTemplate();
      }
    }
  };

  const handleSaveTemplate = async () => {
    if (fields.length === 0) {
      showError("Please add at least one field before saving the template");
      return;
    }

    // Validate required roles have fields
    const requiredRoleNames = ["Direct Seller", "Guarantor"];
    const requiredRoles = roles.filter(r =>
      requiredRoleNames.includes(r.full_name)
    );

    for (const role of requiredRoles) {
      const roleFields = fields.filter(
        f => f.assigned_to_recipient_role === role.id
      );
      if (roleFields.length === 0) {
        showError(
          `Required role "${role.full_name}" must have at least one field assigned. Please add fields for this role.`
        );
        return;
      }
    }

    // Check that both required roles exist
    const hasDirectSeller = roles.some(r => r.full_name === "Direct Seller");
    const hasGuarantor = roles.some(r => r.full_name === "Guarantor");

    if (!hasDirectSeller || !hasGuarantor) {
      showError(
        'Templates must have both "Direct Seller" and "Guarantor" roles.'
      );
      return;
    }

    try {
      setSaving(true);

      // Clear any pending debounced saves
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }

      // Force a final save of current fields
      queueSave(currentFieldsRef.current);

      // Wait for any pending saves to complete
      while (saveInProgressRef.current || saveQueueRef.current.length > 0) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // For documents, save directly without group selection
      // For templates, show group selection modal
      if (isEditingDocument) {
        // Documents don't get assigned to groups, just navigate back
        navigate("/esignature/documents");
        showSuccess("Document saved successfully!");
        setSaving(false);
      } else {
        // Templates can be assigned to groups
        await loadAvailableGroups();
        setShowGroupModal(true);
        setSaving(false);
      }
    } catch (error) {
      handleGenericError(error, "Failed to save template");
      setSaving(false);
    }
  };

  const loadAvailableGroups = async () => {
    try {
      const jwt = await getVerifiedJWT();

      // Fetch all available groups
      const groupsResponse = await fetch(getApiEndpoint("/v1/groups/owned"), {
        headers: {
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (!groupsResponse.ok) {
        throw new Error(
          `Failed to fetch groups: ${groupsResponse.status} ${groupsResponse.statusText}`
        );
      }

      const groupsData = await groupsResponse.json();

      if (groupsData.success) {
        setAvailableGroups(groupsData.data || []);
      } else {
        showError("Failed to load groups");
        setAvailableGroups([]);
        return;
      }

      // Fetch currently assigned groups for this template
      const assignedResponse = await fetch(
        getApiEndpoint(`/v1/esignature/templates/${templateId}/groups`),
        {
          headers: {
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (assignedResponse.ok) {
        const assignedData = await assignedResponse.json();
        if (
          assignedData.success &&
          assignedData.data &&
          assignedData.data.groups &&
          Array.isArray(assignedData.data.groups)
        ) {
          // Extract group IDs from the assigned groups (groups are Group entities, not TemplateGroup)
          const assignedGroupIds = assignedData.data.groups.map(g => g.id);
          setSelectedGroups(assignedGroupIds);
        } else {
          // No groups assigned yet or data is not in expected format
          setSelectedGroups([]);
        }
      } else {
        // If endpoint doesn't exist or returns error, just set empty array
        setSelectedGroups([]);
      }
    } catch (error) {
      handleGenericError(error, "Failed to load groups");
      setAvailableGroups([]);
    }
  };

  const handleSaveGroupPermissions = async () => {
    try {
      setSaving(true);
      const jwt = await getVerifiedJWT();

      // Save group permissions
      const response = await fetch(
        getApiEndpoint(`/v1/esignature/templates/${templateId}/groups`),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({ groupIds: selectedGroups }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to save group permissions");
      }

      // Navigate back and show success message
      navigate("/esignature/templates");
      showSuccess("Template saved successfully!");
    } catch (error) {
      handleGenericError(error, "Failed to save group permissions");
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading-state">Loading template...</div>;
  }

  const saveRolesToTemplate = async rolesToSave => {
    try {
      const jwt = await getVerifiedJWT();
      await esignatureService.updateTemplate(
        templateId,
        { roles_metadata: rolesToSave },
        jwt
      );
    } catch (error) {}
  };

  const handleAddRole = newRole => {
    const role = {
      id: `role-${Date.now()}`,
      role: newRole.type,
      full_name: newRole.name,
      email: `${newRole.name.toLowerCase().replace(/\s+/g, "")}@example.com`,
      color: newRole.color,
      signing_order: roles.length + 1,
    };
    const updatedRoles = [...roles, role];
    setRoles(updatedRoles);
    setSelectedRole(role);
    setShowAddRoleModal(false);
    saveRolesToTemplate(updatedRoles);
  };

  const handleDeleteRole = roleToDelete => {
    // Check if this is a required role
    const requiredRoleNames = ["Direct Seller", "Guarantor"];
    if (requiredRoleNames.includes(roleToDelete.full_name)) {
      alert(`Cannot delete required role: ${roleToDelete.full_name}`);
      return;
    }

    const fieldsWithRole = fields.filter(
      f => f.assigned_to_recipient_role === roleToDelete.id
    );

    if (fieldsWithRole.length > 0) {
      alert(
        `Cannot delete role "${roleToDelete.full_name}" because it has ${fieldsWithRole.length} field(s) assigned to it. Please delete or reassign those fields first.`
      );
      return;
    }

    const updatedRoles = roles.filter(r => r.id !== roleToDelete.id);
    setRoles(updatedRoles);
    saveRolesToTemplate(updatedRoles);

    if (selectedRole?.id === roleToDelete.id && updatedRoles.length > 0) {
      setSelectedRole(updatedRoles[0]);
    }
  };

  const handleReorderRoles = reorderedRoles => {
    setRoles(reorderedRoles);
    saveRolesToTemplate(reorderedRoles);
  };

  const handlePageNavigation = () => {
    const pageNum = parseInt(pageInputValue, 10);
    if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= numPages) {
      if (pdfViewerRef.current?.scrollToPage) {
        pdfViewerRef.current.scrollToPage(pageNum);
      }
    } else {
      // Reset to current page if invalid input
      setPageInputValue(currentPage.toString());
    }
  };

  if (!template) {
    return <div className="error-state">Template not found</div>;
  }

  return (
    <div
      className="document-editor"
      style={{ display: "flex", flexDirection: "column", height: "100vh" }}
    >
      <div
        className="editor-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "16px 24px",
          borderBottom: "1px solid #e0e0e0",
          backgroundColor: "white",
        }}
      >
        <div className="header-left">
          <button
            className="back-btn"
            onClick={() =>
              navigate(isEditingDocument ? "/admin" : "/esignature/templates")
            }
            style={{
              padding: "8px 16px",
              border: "1px solid #ddd",
              borderRadius: "6px",
              background: "white",
              color: "#666",
              fontSize: "14px",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
          >
            ← Back to {isEditingDocument ? "Admin" : "Templates"}
          </button>
        </div>
        <div className="header-center" style={{ flex: 1, textAlign: "center" }}>
          <h1 style={{ margin: 0, fontSize: "20px", fontWeight: 600 }}>
            {template.name || template.title}
          </h1>
        </div>
        <div className="header-actions">
          <button
            className="btn-primary"
            onClick={handleSaveTemplate}
            disabled={saving}
            style={{
              padding: "10px 24px",
              border: "none",
              borderRadius: "6px",
              background: saving ? "#ccc" : "#d0521f",
              color: "white",
              fontSize: "15px",
              fontWeight: 600,
              cursor: saving ? "not-allowed" : "pointer",
              transition: "all 0.2s",
            }}
          >
            {saving ? "Processing..." : "Next"}
          </button>
        </div>
      </div>

      {/* Three-column layout */}
      <div
        className="editor-body"
        style={{ display: "flex", flex: 1, minHeight: 0, overflow: "hidden" }}
      >
        {/* Left sidebar - Roles and Properties */}
        <CombinedSidebar
          roles={roles}
          selectedRole={selectedRole}
          onSelectRole={handleSelectRole}
          onAddRole={() => setShowAddRoleModal(true)}
          onDeleteRole={handleDeleteRole}
          onEditRole={handleEditRole}
          onReorderRoles={handleReorderRoles}
          field={selectedField}
          onUpdate={handleFieldUpdate}
          onDelete={handleFieldDelete}
          onFieldTypeChange={setLastFieldType}
          lastFieldType={lastFieldType}
        />

        {/* Center - PDF Viewer */}
        <PDFViewer
          ref={pdfViewerRef}
          pdfUrl={template.storage_path}
          fields={fields}
          onNumPagesChange={setNumPages}
          onCurrentPageChange={page => {
            setCurrentPage(page);
            setPageInputValue(page.toString());
          }}
          onPageCanvasesChange={setPageCanvases}
          showPageNav={false}
          onFieldAdd={
            selectedRole?.role !== "cc"
              ? handleFieldAdd
              : () => {
                  setShowCCWarning(true);
                }
          }
          onFieldMove={handleFieldMove}
          onFieldSelect={setSelectedField}
          selectedField={selectedField}
          selectedRecipient={selectedRole}
          recipients={roles}
        />

        {/* Right sidebar - Pages Navigation */}
        <div
          style={{
            width: "300px",
            minWidth: "300px",
            maxWidth: "300px",
            borderLeft: "2px solid #e0e0e0",
            backgroundColor: "white",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "12px 16px",
              backgroundColor: "#f8f9fa",
              borderBottom: "1px solid #e0e0e0",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
            }}
          >
            <h3
              style={{
                margin: 0,
                fontSize: "16px",
                fontWeight: 700,
                color: "#333",
                letterSpacing: "0.5px",
              }}
            >
              PAGES
            </h3>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <input
                type="text"
                value={pageInputValue}
                onChange={e => setPageInputValue(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter") {
                    handlePageNavigation();
                    e.target.blur();
                  }
                }}
                onBlur={handlePageNavigation}
                onClick={e => e.target.select()}
                style={{
                  width: "40px",
                  padding: "4px 6px",
                  textAlign: "center",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  fontSize: "13px",
                }}
              />
              <span style={{ fontSize: "13px", color: "#666" }}>
                / {numPages || 0}
              </span>
            </div>
          </div>
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              overflowX: "hidden",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              padding: "16px",
            }}
          >
            {/* Page thumbnails */}
            <div
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              {pageCanvases.map(pageCanvas => (
                <div
                  key={pageCanvas.pageNumber}
                  onClick={() => {
                    if (pdfViewerRef.current?.scrollToPage) {
                      pdfViewerRef.current.scrollToPage(pageCanvas.pageNumber);
                    }
                  }}
                  style={{
                    cursor: "pointer",
                    border:
                      currentPage === pageCanvas.pageNumber
                        ? "2px solid #d0521f"
                        : "2px solid #e0e0e0",
                    borderRadius: "6px",
                    padding: "8px",
                    backgroundColor:
                      currentPage === pageCanvas.pageNumber
                        ? "#fcf3ef"
                        : "white",
                    transition: "all 0.2s ease",
                  }}
                >
                  <canvas
                    ref={canvas => {
                      if (canvas && pageCanvas.canvas) {
                        const ctx = canvas.getContext("2d");
                        const thumbScale = 0.15;
                        canvas.width = pageCanvas.canvas.width * thumbScale;
                        canvas.height = pageCanvas.canvas.height * thumbScale;
                        ctx.scale(thumbScale, thumbScale);
                        ctx.drawImage(pageCanvas.canvas, 0, 0);
                      }
                    }}
                    style={{
                      width: "100%",
                      height: "auto",
                      display: "block",
                      borderRadius: "4px",
                    }}
                  />
                  <div
                    style={{
                      marginTop: "6px",
                      fontSize: "12px",
                      textAlign: "center",
                      color:
                        currentPage === pageCanvas.pageNumber
                          ? "#d0521f"
                          : "#666",
                      fontWeight:
                        currentPage === pageCanvas.pageNumber ? 600 : 400,
                    }}
                  >
                    Page {pageCanvas.pageNumber}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {showAddRoleModal && (
        <AddRoleModal
          existingRoles={roles}
          onClose={() => setShowAddRoleModal(false)}
          onAdd={handleAddRole}
        />
      )}

      {showCCWarning && (
        <CCWarningModal onClose={() => setShowCCWarning(false)} />
      )}

      {showEditRoleModal && editingRole && (
        <EditRoleModal
          role={editingRole}
          existingRoles={roles}
          onClose={() => {
            setShowEditRoleModal(false);
            setEditingRole(null);
          }}
          onUpdate={handleUpdateRole}
        />
      )}

      {showGroupModal && (
        <GroupSelectionModal
          groups={availableGroups}
          selectedGroups={selectedGroups}
          onToggleGroup={groupId => {
            setSelectedGroups(prev =>
              prev.includes(groupId)
                ? prev.filter(id => id !== groupId)
                : [...prev, groupId]
            );
          }}
          onClose={() => setShowGroupModal(false)}
          onSave={handleSaveGroupPermissions}
          saving={saving}
        />
      )}
    </div>
  );
};

const CombinedSidebar = ({
  roles,
  selectedRole,
  onSelectRole,
  onAddRole,
  onDeleteRole,
  onEditRole,
  onReorderRoles,
  field,
  onUpdate,
  onFieldTypeChange,
  lastFieldType,
}) => {
  const [draggedRoleIndex, setDraggedRoleIndex] = useState(null);

  const formatRoleType = roleType => {
    if (roleType === "cc") return "CC";
    if (roleType === "signer") return "Signer";
    if (roleType === "approver") return "Approver";
    return roleType.charAt(0).toUpperCase() + roleType.slice(1);
  };

  const handleDragStart = (e, index) => {
    setDraggedRoleIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, dropIndex, onReorder) => {
    e.preventDefault();

    if (draggedRoleIndex === null || draggedRoleIndex === dropIndex) {
      setDraggedRoleIndex(null);
      return;
    }

    const sortedRoles = [...roles].sort(
      (a, b) => (a.signing_order || 0) - (b.signing_order || 0)
    );

    const updated = [...sortedRoles];
    const [draggedItem] = updated.splice(draggedRoleIndex, 1);
    updated.splice(dropIndex, 0, draggedItem);

    // Update signing_order for all items
    const reordered = updated.map((r, idx) => ({
      ...r,
      signing_order: idx + 1,
      display_order: idx + 1,
    }));

    // Call parent to save reordered roles
    if (onReorder) {
      onReorder(reordered);
    }

    setDraggedRoleIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedRoleIndex(null);
  };

  // eslint-disable-next-line no-unused-vars
  const FIELD_TYPES = [
    { value: "signature", label: "Signature" },
    { value: "initial", label: "Initial" },
    { value: "text", label: "Text" },
    { value: "email", label: "Email" },
    { value: "phone", label: "Phone Number" },
    { value: "checkbox", label: "Checkbox" },
    { value: "date", label: "Date" },
  ];

  const getFieldLabel = fieldType => {
    const labelMap = {
      signature: "Signature",
      initial: "Initial",
      text: "Text",
      email: "Email",
      phone: "Phone Number",
      checkbox: "Checkbox",
      date: "Date",
    };
    return labelMap[fieldType] || fieldType;
  };

  // eslint-disable-next-line no-unused-vars
  const handleChange = (key, value) => {
    const updatedField = { ...field, [key]: value };

    // If field type changes, update the label to match
    if (key === "field_type") {
      updatedField.label = getFieldLabel(value);
      // Also update the last field type so new fields will use this type
      if (onFieldTypeChange) {
        onFieldTypeChange(value);
      }
    }

    onUpdate(updatedField);
  };

  const sortedRoles = [...roles].sort(
    (a, b) => (a.signing_order || 0) - (b.signing_order || 0)
  );

  return (
    <div
      className="recipient-sidebar"
      style={{
        width: "300px",
        minWidth: "300px",
        maxWidth: "300px",
        overflow: "hidden",
        borderRight: "2px solid #e0e0e0",
        backgroundColor: "white",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Recipients Section */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          borderBottom: "2px solid #e0e0e0",
          backgroundColor: "white",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <h3
          style={{
            margin: 0,
            padding: "16px",
            fontSize: "16px",
            fontWeight: 700,
            color: "#333",
            letterSpacing: "0.5px",
            backgroundColor: "#f8f9fa",
            borderBottom: "1px solid #e0e0e0",
            flexShrink: 0,
          }}
        >
          RECIPIENTS
        </h3>
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            padding: "8px",
          }}
        >
          {sortedRoles.map((role, index) => (
            <div
              key={role.id}
              className={`recipient-item ${
                selectedRole?.id === role.id ? "selected" : ""
              } ${draggedRoleIndex === index ? "dragging" : ""}`}
              onClick={() => onSelectRole(role)}
              draggable
              onDragStart={e => handleDragStart(e, index)}
              onDragOver={e => handleDragOver(e, index)}
              onDrop={e => handleDrop(e, index, onReorderRoles)}
              onDragEnd={handleDragEnd}
              style={{
                position: "relative",
                opacity: draggedRoleIndex === index ? 0.5 : 1,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "36px",
                  height: "36px",
                  borderRadius: "50%",
                  backgroundColor: role.color,
                  color: "white",
                  fontWeight: 700,
                  fontSize: "16px",
                  flexShrink: 0,
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.15)",
                }}
              >
                {role.signing_order}
              </div>
              <div className="recipient-info" style={{ flex: 1, minWidth: 0 }}>
                <div
                  className="recipient-name"
                  style={{
                    fontWeight: 600,
                    fontSize: "14px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {role.full_name}
                </div>
                <div
                  className="recipient-email"
                  style={{
                    fontSize: "12px",
                    color: "#666",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {formatRoleType(role.role)}
                </div>
              </div>
              <div style={{ display: "flex", gap: "4px" }}>
                <button
                  className="edit-role-btn"
                  onClick={e => onEditRole(role, e)}
                  title="Edit role"
                  style={{
                    width: "24px",
                    height: "24px",
                    borderRadius: "50%",
                    border: "none",
                    background: "#f5f5f5",
                    color: "#333",
                    cursor: "pointer",
                    fontSize: "14px",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  ✎
                </button>
                {roles.length > 1 && (
                  <button
                    className="delete-role-btn"
                    onClick={e => {
                      e.stopPropagation();
                      onDeleteRole(role);
                    }}
                    title="Delete role"
                    style={{
                      width: "24px",
                      height: "24px",
                      borderRadius: "50%",
                      border: "none",
                      background: "#f5f5f5",
                      color: "#e74c3c",
                      cursor: "pointer",
                      fontSize: "18px",
                      fontWeight: "bold",
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
        <button
          className="add-role-btn"
          onClick={onAddRole}
          style={{
            width: "calc(100% - 16px)",
            margin: "0 8px 8px 8px",
            padding: "10px 16px",
            flexShrink: 0,
            backgroundColor: "#d0521f",
            color: "white",
            border: "none",
            borderRadius: "6px",
            fontSize: "14px",
            fontWeight: 600,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            transition: "all 0.2s ease",
            boxShadow: "0 2px 4px rgba(208, 82, 31, 0.2)",
          }}
          onMouseEnter={e => {
            e.currentTarget.style.backgroundColor = "#d0521f";
            e.currentTarget.style.boxShadow =
              "0 4px 8px rgba(208, 82, 31, 0.3)";
            e.currentTarget.style.transform = "translateY(-1px)";
          }}
          onMouseLeave={e => {
            e.currentTarget.style.backgroundColor = "#d0521f";
            e.currentTarget.style.boxShadow =
              "0 2px 4px rgba(208, 82, 31, 0.2)";
            e.currentTarget.style.transform = "translateY(0)";
          }}
        >
          <span style={{ fontSize: "18px", lineHeight: "1" }}>+</span>
          <span>Add Role</span>
        </button>
      </div>

      {/* Fields Section - No scrolling needed */}
      <div
        style={{
          flexShrink: 0,
          borderTop: "2px solid #e0e0e0",
          backgroundColor: "white",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <h3
          style={{
            margin: 0,
            padding: "16px",
            fontSize: "16px",
            fontWeight: 700,
            color: "#333",
            letterSpacing: "0.5px",
            backgroundColor: "#f8f9fa",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          FIELDS
        </h3>
        <div style={{ padding: "8px" }}>
          {/* Signature Fields Group */}
          <div
            onClick={() => onFieldTypeChange("signature")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              padding: "10px 16px",
              cursor: "pointer",
              backgroundColor:
                lastFieldType === "signature" ? "#e8e8e8" : "transparent",
              borderRadius: "6px",
              marginBottom: "2px",
              transition: "background-color 0.15s",
            }}
          >
            <span
              className="material-symbols-outlined"
              style={{ fontSize: "20px", color: "#666" }}
            >
              draw
            </span>
            <span style={{ fontSize: "15px", color: "#333" }}>Signature</span>
          </div>

          <div
            onClick={() => onFieldTypeChange("initial")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              padding: "10px 16px",
              cursor: "pointer",
              backgroundColor:
                lastFieldType === "initial" ? "#e8e8e8" : "transparent",
              borderRadius: "6px",
              marginBottom: "2px",
              transition: "background-color 0.15s",
            }}
          >
            <span
              className="material-symbols-outlined"
              style={{ fontSize: "20px", color: "#666" }}
            >
              edit_note
            </span>
            <span style={{ fontSize: "15px", color: "#333" }}>Initial</span>
          </div>

          <div
            onClick={() => onFieldTypeChange("date")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              padding: "10px 16px",
              cursor: "pointer",
              backgroundColor:
                lastFieldType === "date" ? "#e8e8e8" : "transparent",
              borderRadius: "6px",
              marginBottom: "8px",
              transition: "background-color 0.15s",
            }}
          >
            <span
              className="material-symbols-outlined"
              style={{ fontSize: "20px", color: "#666" }}
            >
              calendar_today
            </span>
            <span style={{ fontSize: "15px", color: "#333" }}>Date Signed</span>
          </div>

          {/* Divider */}
          <div
            style={{
              height: "1px",
              backgroundColor: "#e0e0e0",
              margin: "8px 0",
            }}
          />

          {/* Text Fields Group */}
          <div
            onClick={() => onFieldTypeChange("text")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              padding: "10px 16px",
              cursor: "pointer",
              backgroundColor:
                lastFieldType === "text" ? "#e8e8e8" : "transparent",
              borderRadius: "6px",
              marginBottom: "2px",
              transition: "background-color 0.15s",
            }}
          >
            <span
              className="material-symbols-outlined"
              style={{ fontSize: "20px", color: "#666" }}
            >
              text_fields
            </span>
            <span style={{ fontSize: "15px", color: "#333" }}>Text</span>
          </div>

          <div
            onClick={() => onFieldTypeChange("email")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              padding: "10px 16px",
              cursor: "pointer",
              backgroundColor:
                lastFieldType === "email" ? "#e8e8e8" : "transparent",
              borderRadius: "6px",
              marginBottom: "2px",
              transition: "background-color 0.15s",
            }}
          >
            <span
              className="material-symbols-outlined"
              style={{ fontSize: "20px", color: "#666" }}
            >
              mail
            </span>
            <span style={{ fontSize: "15px", color: "#333" }}>Email</span>
          </div>

          <div
            onClick={() => onFieldTypeChange("phone")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              padding: "10px 16px",
              cursor: "pointer",
              backgroundColor:
                lastFieldType === "phone" ? "#e8e8e8" : "transparent",
              borderRadius: "6px",
              marginBottom: "8px",
              transition: "background-color 0.15s",
            }}
          >
            <span
              className="material-symbols-outlined"
              style={{ fontSize: "20px", color: "#666" }}
            >
              phone
            </span>
            <span style={{ fontSize: "15px", color: "#333" }}>Phone</span>
          </div>

          {/* Divider */}
          <div
            style={{
              height: "1px",
              backgroundColor: "#e0e0e0",
              margin: "8px 0",
            }}
          />

          {/* Other Fields */}
          <div
            onClick={() => onFieldTypeChange("checkbox")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              padding: "10px 16px",
              cursor: "pointer",
              backgroundColor:
                lastFieldType === "checkbox" ? "#e8e8e8" : "transparent",
              borderRadius: "6px",
              marginBottom: "2px",
              transition: "background-color 0.15s",
            }}
          >
            <span
              className="material-symbols-outlined"
              style={{ fontSize: "20px", color: "#666" }}
            >
              check_box
            </span>
            <span style={{ fontSize: "15px", color: "#333" }}>Checkbox</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const AddRoleModal = ({ existingRoles, onClose, onAdd }) => {
  const [roleName, setRoleName] = useState("");
  const [roleType, setRoleType] = useState("signer");
  const [selectedColor, setSelectedColor] = useState(
    DEFAULT_COLORS[existingRoles.length % DEFAULT_COLORS.length]
  );

  const handleSubmit = e => {
    e.preventDefault();

    if (!roleName.trim()) {
      alert("Please enter a role name");
      return;
    }

    if (
      existingRoles.some(r => r.role.toLowerCase() === roleName.toLowerCase())
    ) {
      alert("A role with this name already exists");
      return;
    }

    onAdd({ name: roleName.trim(), type: roleType, color: selectedColor });
  };

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        className="modal-content"
        onClick={e => e.stopPropagation()}
        style={{
          backgroundColor: "white",
          borderRadius: "12px",
          padding: "32px",
          maxWidth: "480px",
          width: "90%",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
        }}
      >
        <h2
          style={{
            margin: "0 0 24px 0",
            fontSize: "24px",
            fontWeight: 600,
            color: "#333",
          }}
        >
          Add Role
        </h2>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#555",
              }}
            >
              Role Name *
            </label>
            <input
              type="text"
              value={roleName}
              onChange={e => setRoleName(e.target.value)}
              placeholder="e.g., Legal Counsel, CFO, Board Member"
              autoFocus
              required
              style={{
                width: "100%",
                padding: "10px 12px",
                fontSize: "15px",
                border: "2px solid #ddd",
                borderRadius: "6px",
                outline: "none",
                transition: "border-color 0.2s",
                boxSizing: "border-box",
              }}
              onFocus={e => (e.target.style.borderColor = "#d0521f")}
              onBlur={e => (e.target.style.borderColor = "#ddd")}
            />
          </div>
          <div style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#555",
              }}
            >
              Role Type *
            </label>
            <select
              value={roleType}
              onChange={e => setRoleType(e.target.value)}
              required
              style={{
                width: "100%",
                padding: "10px 12px",
                fontSize: "15px",
                border: "2px solid #ddd",
                borderRadius: "6px",
                outline: "none",
                backgroundColor: "white",
                cursor: "pointer",
                boxSizing: "border-box",
              }}
            >
              <option value="signer">Needs to Sign</option>
              <option value="approver">Approver</option>
              <option value="cc">Receives a Copy (CC)</option>
            </select>
          </div>
          <div style={{ marginBottom: "28px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "12px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#555",
              }}
            >
              Color
            </label>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: "10px",
              }}
            >
              {DEFAULT_COLORS.map(color => (
                <div
                  key={color}
                  onClick={() => setSelectedColor(color)}
                  style={{
                    width: "100%",
                    paddingBottom: "100%",
                    position: "relative",
                    backgroundColor: color,
                    borderRadius: "8px",
                    cursor: "pointer",
                    border:
                      selectedColor === color
                        ? "3px solid #333"
                        : "3px solid transparent",
                    boxShadow:
                      selectedColor === color
                        ? "0 4px 12px rgba(0, 0, 0, 0.2)"
                        : "0 2px 4px rgba(0, 0, 0, 0.1)",
                    transition: "all 0.2s ease",
                    transform:
                      selectedColor === color ? "scale(1.05)" : "scale(1)",
                  }}
                >
                  {selectedColor === color && (
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        color: "white",
                        fontSize: "24px",
                        fontWeight: "bold",
                        textShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
                      }}
                    >
                      ✓
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div
            style={{
              display: "flex",
              gap: "12px",
              justifyContent: "flex-end",
              paddingTop: "8px",
            }}
          >
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: "10px 24px",
                fontSize: "15px",
                fontWeight: 500,
                border: "2px solid #ddd",
                borderRadius: "6px",
                backgroundColor: "white",
                color: "#666",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = "#999";
                e.currentTarget.style.color = "#333";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = "#ddd";
                e.currentTarget.style.color = "#666";
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                padding: "10px 24px",
                fontSize: "15px",
                fontWeight: 600,
                border: "none",
                borderRadius: "6px",
                backgroundColor: "#d0521f",
                color: "white",
                cursor: "pointer",
                transition: "all 0.2s",
                boxShadow: "0 4px 12px rgba(208, 82, 31, 0.3)",
              }}
              onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = "#d0521f";
                e.currentTarget.style.boxShadow =
                  "0 6px 16px rgba(208, 82, 31, 0.4)";
                e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = "#d0521f";
                e.currentTarget.style.boxShadow =
                  "0 4px 12px rgba(208, 82, 31, 0.3)";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              Add Role
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditRoleModal = ({ role, existingRoles, onClose, onUpdate }) => {
  const [roleName, setRoleName] = useState(role.full_name);
  const [roleType, setRoleType] = useState(role.role);
  const [selectedColor, setSelectedColor] = useState(role.color);

  const handleSubmit = e => {
    e.preventDefault();

    if (!roleName.trim()) {
      alert("Please enter a role name");
      return;
    }

    // Check for duplicate role names (excluding current role)
    if (
      existingRoles.some(
        r => r.id !== role.id && r.role.toLowerCase() === roleName.toLowerCase()
      )
    ) {
      alert("A role with this name already exists");
      return;
    }

    onUpdate({ name: roleName.trim(), type: roleType, color: selectedColor });
  };

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        className="modal-content"
        onClick={e => e.stopPropagation()}
        style={{
          backgroundColor: "white",
          borderRadius: "12px",
          padding: "32px",
          maxWidth: "480px",
          width: "90%",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
        }}
      >
        <h2
          style={{
            margin: "0 0 24px 0",
            fontSize: "24px",
            fontWeight: 600,
            color: "#333",
          }}
        >
          Edit Role
        </h2>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#555",
              }}
            >
              Role Name *
            </label>
            <input
              type="text"
              value={roleName}
              onChange={e => setRoleName(e.target.value)}
              placeholder="e.g., Legal Counsel, CFO, Board Member"
              autoFocus
              required
              style={{
                width: "100%",
                padding: "10px 12px",
                fontSize: "15px",
                border: "2px solid #ddd",
                borderRadius: "6px",
                outline: "none",
                transition: "border-color 0.2s",
                boxSizing: "border-box",
              }}
              onFocus={e => (e.target.style.borderColor = "#d0521f")}
              onBlur={e => (e.target.style.borderColor = "#ddd")}
            />
          </div>
          <div style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#555",
              }}
            >
              Role Type *
            </label>
            <select
              value={roleType}
              onChange={e => setRoleType(e.target.value)}
              required
              style={{
                width: "100%",
                padding: "10px 12px",
                fontSize: "15px",
                border: "2px solid #ddd",
                borderRadius: "6px",
                outline: "none",
                backgroundColor: "white",
                cursor: "pointer",
                boxSizing: "border-box",
              }}
            >
              <option value="signer">Needs to Sign</option>
              <option value="approver">Approver</option>
              <option value="cc">Receives a Copy (CC)</option>
            </select>
          </div>
          <div style={{ marginBottom: "28px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "12px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#555",
              }}
            >
              Color
            </label>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: "10px",
              }}
            >
              {DEFAULT_COLORS.map(color => (
                <div
                  key={color}
                  onClick={() => setSelectedColor(color)}
                  style={{
                    width: "100%",
                    paddingBottom: "100%",
                    position: "relative",
                    backgroundColor: color,
                    borderRadius: "8px",
                    cursor: "pointer",
                    border:
                      selectedColor === color
                        ? "3px solid #333"
                        : "3px solid transparent",
                    boxShadow:
                      selectedColor === color
                        ? "0 4px 12px rgba(0, 0, 0, 0.2)"
                        : "0 2px 4px rgba(0, 0, 0, 0.1)",
                    transition: "all 0.2s ease",
                    transform:
                      selectedColor === color ? "scale(1.05)" : "scale(1)",
                  }}
                >
                  {selectedColor === color && (
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        color: "white",
                        fontSize: "24px",
                        fontWeight: "bold",
                        textShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
                      }}
                    >
                      ✓
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div
            style={{
              display: "flex",
              gap: "12px",
              justifyContent: "flex-end",
              paddingTop: "8px",
            }}
          >
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: "10px 24px",
                fontSize: "15px",
                fontWeight: 500,
                border: "2px solid #ddd",
                borderRadius: "6px",
                backgroundColor: "white",
                color: "#666",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = "#999";
                e.currentTarget.style.color = "#333";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = "#ddd";
                e.currentTarget.style.color = "#666";
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                padding: "10px 24px",
                fontSize: "15px",
                fontWeight: 600,
                border: "none",
                borderRadius: "6px",
                backgroundColor: "#d0521f",
                color: "white",
                cursor: "pointer",
                transition: "all 0.2s",
                boxShadow: "0 4px 12px rgba(208, 82, 31, 0.3)",
              }}
              onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = "#d0521f";
                e.currentTarget.style.boxShadow =
                  "0 6px 16px rgba(208, 82, 31, 0.4)";
                e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = "#d0521f";
                e.currentTarget.style.boxShadow =
                  "0 4px 12px rgba(208, 82, 31, 0.3)";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CCWarningModal = ({ onClose }) => {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: "400px" }}
      >
        <h2 style={{ color: "#e74c3c" }}>⚠️ Cannot Assign Field</h2>
        <p style={{ marginBottom: "20px", lineHeight: "1.6" }}>
          Can't assign role to copy receiver please change role
        </p>
        <div className="modal-actions">
          <button
            className="btn-primary"
            onClick={onClose}
            style={{ width: "100%" }}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

const GroupSelectionModal = ({
  groups,
  selectedGroups,
  onToggleGroup,
  onClose,
  onSave,
  saving,
}) => {
  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        className="modal-content"
        onClick={e => e.stopPropagation()}
        style={{
          backgroundColor: "white",
          borderRadius: "12px",
          padding: "32px",
          maxWidth: "600px",
          width: "90%",
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
        }}
      >
        <h2
          style={{
            margin: "0 0 12px 0",
            fontSize: "24px",
            fontWeight: 600,
            color: "#333",
          }}
        >
          Select Groups
        </h2>
        <p
          style={{
            margin: "0 0 24px 0",
            fontSize: "14px",
            color: "#666",
            lineHeight: "1.6",
          }}
        >
          Choose which groups should have access to view and send this template.
        </p>

        <div
          style={{
            flex: 1,
            overflowY: "auto",
            marginBottom: "24px",
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            padding: "12px",
          }}
        >
          {groups.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "40px 20px",
                color: "#999",
              }}
            >
              No groups available. Create groups in the Organization tab.
            </div>
          ) : (
            groups.map(group => (
              <div
                key={group.id}
                onClick={() => onToggleGroup(group.id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "14px",
                  marginBottom: "8px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  border: selectedGroups.includes(group.id)
                    ? "2px solid #d0521f"
                    : "2px solid #e0e0e0",
                  backgroundColor: selectedGroups.includes(group.id)
                    ? "#fcf3ef"
                    : "white",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={e => {
                  if (!selectedGroups.includes(group.id)) {
                    e.currentTarget.style.borderColor = "#ccc";
                  }
                }}
                onMouseLeave={e => {
                  if (!selectedGroups.includes(group.id)) {
                    e.currentTarget.style.borderColor = "#e0e0e0";
                  }
                }}
              >
                <div
                  style={{
                    width: "24px",
                    height: "24px",
                    borderRadius: "6px",
                    border: selectedGroups.includes(group.id)
                      ? "2px solid #d0521f"
                      : "2px solid #ccc",
                    backgroundColor: selectedGroups.includes(group.id)
                      ? "#d0521f"
                      : "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginRight: "12px",
                    transition: "all 0.2s ease",
                  }}
                >
                  {selectedGroups.includes(group.id) && (
                    <span
                      style={{
                        color: "white",
                        fontSize: "16px",
                        fontWeight: "bold",
                      }}
                    >
                      ✓
                    </span>
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: "15px",
                      color: "#333",
                      marginBottom: "2px",
                    }}
                  >
                    {group.name}
                  </div>
                  {group.rep_type && (
                    <div style={{ fontSize: "13px", color: "#666" }}>
                      {group.rep_type.name}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        <div
          style={{
            display: "flex",
            gap: "12px",
            justifyContent: "flex-end",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            style={{
              padding: "12px 28px",
              fontSize: "15px",
              fontWeight: 500,
              border: "2px solid #ddd",
              borderRadius: "6px",
              backgroundColor: "white",
              color: "#666",
              cursor: saving ? "not-allowed" : "pointer",
              opacity: saving ? 0.6 : 1,
              transition: "all 0.2s",
            }}
            onMouseEnter={e => {
              if (!saving) {
                e.currentTarget.style.borderColor = "#999";
                e.currentTarget.style.color = "#333";
              }
            }}
            onMouseLeave={e => {
              if (!saving) {
                e.currentTarget.style.borderColor = "#ddd";
                e.currentTarget.style.color = "#666";
              }
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            style={{
              padding: "12px 28px",
              fontSize: "15px",
              fontWeight: 600,
              border: "none",
              borderRadius: "6px",
              backgroundColor: saving ? "#ccc" : "#d0521f",
              color: "white",
              cursor: saving ? "not-allowed" : "pointer",
              transition: "all 0.2s",
              boxShadow: saving ? "none" : "0 4px 12px rgba(208, 82, 31, 0.3)",
            }}
            onMouseEnter={e => {
              if (!saving) {
                e.currentTarget.style.backgroundColor = "#d0521f";
                e.currentTarget.style.boxShadow =
                  "0 6px 16px rgba(208, 82, 31, 0.4)";
                e.currentTarget.style.transform = "translateY(-1px)";
              }
            }}
            onMouseLeave={e => {
              if (!saving) {
                e.currentTarget.style.backgroundColor = "#d0521f";
                e.currentTarget.style.boxShadow =
                  "0 4px 12px rgba(208, 82, 31, 0.3)";
                e.currentTarget.style.transform = "translateY(0)";
              }
            }}
          >
            {saving ? "Saving..." : "Save & Close"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateEditor;
